/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { SPView, PCView } from "components/common/MediaQuery";
import React from "react";
import { Redirect } from "react-router-dom";

import {
  getEventDashboardPermissionErrorResolution,
  isEventDashboardPermissionError,
} from "../eventDashboardPermissionError";
import { useFetchAdminTicket } from "./useFetchAdminTicket";

export const AdminTicketDashboard: React.FC = () => {
  const {
    displayTicketSummary,
    overallSummary,
    isLoading,
    isValidating,
    error,
    lastUpdated,
    mutate,
    eventTitle,
  } = useFetchAdminTicket();

  const hasRedeemable = overallSummary.redeemableCount > 0;

  const handleRefresh = async () => {
    try {
      await mutate();
    } catch (error) {
      console.error("Failed to refresh:", error);
      alert("データの更新に失敗しました。再度お試しください。");
    }
  };

  const permissionErrorResolution = getEventDashboardPermissionErrorResolution([
    error,
  ]);

  if (permissionErrorResolution === "redirect") {
    return <Redirect to="/" />;
  }

  // 初回データ取得前にエラーが発生した場合は全画面エラーを表示
  if (error && !lastUpdated) {
    return (
      <div css={styles.container}>
        <p css={styles.errorText}>{getErrorMessage(error)}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div css={styles.container}>
        <p
          style={{ textAlign: "center", marginTop: "40px", color: "#ffffffff" }}
          // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
        >
          読み込み中...
        </p>
      </div>
    );
  }

  return (
    <div css={styles.container}>
      {eventTitle ? (
        <h1 css={styles.eventTitle}>{eventTitle}</h1>
      ) : (
        // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
        <h1 css={styles.headerText}>チケット状況</h1>
      )}
      <div css={styles.headerArea}>
        <div css={styles.lastUpdatedContainer}>
          {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
          <span>
            データ取得日時:
            {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
            {lastUpdated ? lastUpdated.toLocaleString() : "未取得"}
          </span>
          <button
            onClick={handleRefresh}
            css={styles.refreshButton}
            disabled={isLoading || isValidating}
          >
            {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
            {isLoading || isValidating ? "更新中..." : "更新"}
          </button>
        </div>
        {error && (
          <p css={styles.backgroundErrorText}>{getErrorMessage(error)}</p>
        )}
      </div>
      <PCView>
        {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
        <h2 css={styles.sectionHeader}>全体集計</h2>
        <div css={styles.tableWrapper}>
          <table css={styles.table}>
            <thead>
              <tr css={styles.headerRow}>
                {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                <th>購入チケット総数</th>
                {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                <th>チケットもぎり総数</th>
                {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                <th>チケットもぎり全体率</th>
                {hasRedeemable && (
                  <React.Fragment>
                    {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                    <th>特典つきチケット総数</th>
                    {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                    <th>特典もぎり総数</th>
                    {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                    <th>特典もぎり全体率</th>
                  </React.Fragment>
                )}
              </tr>
            </thead>
            <tbody>
              <tr css={styles.rowItem}>
                <td css={styles.rowText}>{overallSummary.purchasedCount}</td>
                <td css={styles.rowText}>{overallSummary.checkedInCount}</td>
                <td css={styles.rowText}>{overallSummary.checkedInRate}%</td>
                {hasRedeemable && (
                  <React.Fragment>
                    <td css={styles.rowText}>
                      {overallSummary.redeemableCount}
                    </td>
                    <td css={styles.rowText}>{overallSummary.redeemedCount}</td>
                    <td css={styles.rowText}>{overallSummary.redeemedRate}%</td>
                  </React.Fragment>
                )}
              </tr>
            </tbody>
          </table>
        </div>
        {/* eslint-disable no-restricted-syntax -- i18n 未対応 */}
        <h2 css={styles.sectionHeader}>
          チケット別 ({displayTicketSummary?.length ?? 0}件)
        </h2>
        {/* eslint-enable no-restricted-syntax */}
        <div css={styles.tableWrapper}>
          <table css={styles.table}>
            <thead>
              <tr css={styles.headerRow}>
                {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                <th>チケット名</th>
                {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                <th>購入数</th>
                {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                <th>チケットもぎり数</th>
                {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                <th>チケットもぎり率</th>
                {hasRedeemable && (
                  <React.Fragment>
                    {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                    <th>特典もぎり数</th>
                    {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                    <th>特典もぎり率</th>
                  </React.Fragment>
                )}
              </tr>
            </thead>

            <tbody>
              {displayTicketSummary?.map((t) => (
                <tr key={t.ticketId} css={styles.rowItem}>
                  <td style={{ fontWeight: "bold" }}>{t.ticketName}</td>
                  <td>{t.purchasedCount}</td>
                  <td css={styles.rowText}>{t.checkedInCount}</td>
                  <td css={styles.rowText}>{t.checkedInRate}%</td>
                  {hasRedeemable && (
                    <React.Fragment>
                      <td>{t.redeemableCount > 0 ? t.redeemedCount : "-"}</td>
                      <td css={styles.rowText}>
                        {t.redeemableCount > 0 ? `${t.redeemedRate}%` : "-"}
                      </td>
                    </React.Fragment>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {displayTicketSummary?.length === 0 && (
          // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
          <p style={{ textAlign: "center", marginTop: "40px", color: "#666" }}>
            データが見つかりません
          </p>
        )}
      </PCView>
      <SPView>
        {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
        <h2 css={styles.sectionHeader}>全体集計</h2>
        <div css={styles.spCardList}>
          <div css={styles.spCard}>
            <div css={styles.spDetailGrid}>
              <div css={styles.spDetailItem}>
                {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                <div css={styles.spLabel}>チケットもぎり</div>
                <div css={styles.spValue}>
                  {overallSummary.checkedInRate}%
                  <span css={styles.spSubUnit}>
                    ({overallSummary.checkedInCount} /{" "}
                    {overallSummary.purchasedCount})
                  </span>
                </div>
                <div css={styles.progressBarBg}>
                  <div css={styles.progressBar(overallSummary.checkedInRate)} />
                </div>
              </div>
              {hasRedeemable && (
                <div css={styles.spDetailItem}>
                  {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                  <div css={styles.spLabel}>特典もぎり</div>
                  <div css={styles.spValue}>
                    {overallSummary.redeemedRate}%
                    <span css={styles.spSubUnit}>
                      ({overallSummary.redeemedCount} /{" "}
                      {overallSummary.redeemableCount})
                    </span>
                  </div>
                  <div css={styles.progressBarBg}>
                    <div
                      css={styles.progressBar(overallSummary.redeemedRate)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* eslint-disable no-restricted-syntax -- i18n 未対応 */}
        <h2 css={styles.sectionHeader}>
          チケット別 ({displayTicketSummary?.length ?? 0}件)
        </h2>
        {/* eslint-enable no-restricted-syntax */}
        <div css={styles.spCardList}>
          {displayTicketSummary && displayTicketSummary.length === 0 && (
            <p
              style={{ textAlign: "center", marginTop: "40px", color: "#666" }}
              // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
            >
              データが見つかりません
            </p>
          )}
          {displayTicketSummary?.map((t) => (
            <div key={t.ticketId} css={styles.spCard}>
              <div css={styles.spCardHeader}>
                <div css={styles.spTicketName}>{t.ticketName}</div>
              </div>

              <div css={styles.spDetailGrid}>
                {/* チケットもぎりセクション */}
                <div css={styles.spDetailItem}>
                  {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                  <div css={styles.spLabel}>チケットもぎり</div>
                  <div css={styles.spValue}>
                    {t.checkedInRate}%
                    <span css={styles.spSubUnit}>
                      ({t.checkedInCount} / {t.purchasedCount})
                    </span>
                  </div>
                  <div css={styles.progressBarBg}>
                    <div css={styles.progressBar(t.checkedInRate)} />
                  </div>
                </div>

                {/* 特典もぎりセクション */}
                {t.redeemableCount > 0 && (
                  <div css={styles.spDetailItem}>
                    {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                    <div css={styles.spLabel}>特典もぎり</div>
                    <div css={styles.spValue}>
                      {t.redeemedRate}%
                      <span css={styles.spSubUnit}>
                        ({t.redeemedCount} / {t.redeemableCount})
                      </span>
                    </div>
                    <div css={styles.progressBarBg}>
                      <div css={styles.progressBar(t.redeemedRate)} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </SPView>
    </div>
  );
};

const getErrorMessage = (error: unknown): string => {
  return isEventDashboardPermissionError(error)
    ? "このイベントの閲覧権限がありません。"
    : "データの取得に失敗しました。";
};

//TODO: 仮のデザイン
const styles = {
  container: css({
    backgroundColor: "#000000",
    color: "#ffffff",
    padding: "24px",
    minHeight: "100vh",
    fontFamily: "sans-serif",
  }),
  eventTitle: css({
    color: "#ffffff",
    fontSize: "1.75rem",
    fontWeight: "bold",
    marginBottom: "8px",
  }),
  headerSubtitle: css({
    color: "#a0a0a0",
    fontSize: "1.1rem",
    fontWeight: "500",
    marginBottom: "20px",
  }),
  headerText: css({
    color: "#ffffff",
    fontSize: "1.5rem",
    fontWeight: "bold",
    marginBottom: "20px",
  }),
  sectionHeader: css({
    color: "#ffffff",
    fontSize: "1.15rem",
    fontWeight: "bold",
    marginTop: "32px",
    marginBottom: "12px",
    paddingBottom: "8px",
    borderBottom: "1px solid #333",
  }),
  headerArea: css({
    position: "relative",
    marginBottom: "32px",
  }),
  lastUpdatedContainer: css({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    fontSize: "0.85rem",
    color: "#ffffff",
    marginBottom: 0,
  }),
  backgroundErrorText: css({
    position: "absolute",
    bottom: "-20px",
    left: 0,
    color: "#ff4d4f",
    fontSize: "0.85rem",
    fontWeight: "bold",
    margin: 0,
  }),
  refreshButton: css({
    padding: "6px 12px",
    backgroundColor: "#1f1f1f",
    color: "#ffffff",
    border: "1px solid #aaaaaa",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "0.8rem",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "#333333",
    },
    "&:active": {
      backgroundColor: "#000000",
    },
  }),
  tableWrapper: css({
    width: "100%",
    marginTop: "20px",
    backgroundColor: "#111111",
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid #333",
  }),
  table: css({
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  }),
  headerRow: css({
    backgroundColor: "#1f1f1f",
    borderBottom: "2px solid #333",
    "& th": {
      padding: "12px 16px",
      fontSize: "0.85rem",
      color: "#9ca3af",
      fontWeight: "bold",
      textAlign: "left",
    },
    // idと名前の列を広くする
    "& th:first-of-type": { width: "2fr" },
    "& th:nth-of-type(2)": { width: "2fr" },
  }),
  rowItem: css({
    borderBottom: "1px solid #222",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "#1a1a1a",
    },
    "& td": {
      padding: "16px",
      fontSize: "0.95rem",
      verticalAlign: "middle",
    },
  }),
  rowText: css({
    color: "#fff",
    fontSize: "0.9rem",
  }),
  spCardList: css({
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  }),
  spCard: css({
    backgroundColor: "#111111",
    borderRadius: "12px",
    padding: "16px",
    border: "1px solid #333",
  }),
  spCardHeader: css({
    borderBottom: "1px solid #222",
    paddingBottom: "12px",
    marginBottom: "12px",
  }),
  spTicketId: css({
    fontSize: "0.75rem",
    color: "#ffffffff",
    display: "block",
    marginBottom: "4px",
  }),
  spTicketName: css({
    fontSize: "1.1rem",
    fontWeight: "bold",
    color: "#fff",
  }),
  spLabel: css({
    fontSize: "1.0rem",
    color: "#ffffffff",
    marginBottom: "4px",
  }),
  spDetailGrid: css({
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  }),
  spDetailItem: css({
    backgroundColor: "#1a1a1a",
    padding: "10px",
    borderRadius: "8px",
  }),
  spValue: css({
    fontSize: "1rem",
    fontWeight: "bold",
  }),
  spSubUnit: css({
    fontSize: "0.85rem",
    color: "#a0a0a0",
    marginLeft: "8px",
  }),
  progressBarBg: css({
    width: "100%",
    height: "4px",
    backgroundColor: "#333",
    borderRadius: "2px",
    marginTop: "8px",
    overflow: "hidden",
  }),
  progressBar: (rate: number) => {
    const clampedRate = Math.max(0, Math.min(rate, 100)); // 0%未満と100%を超える値をクランプ
    return css({
      width: `${clampedRate}%`,
      height: "100%",
      backgroundColor: rate >= 100 ? "#52c41a" : "#3b82f6",
      transition: "width 0.3s ease-in-out",
    });
  },
  errorText: css({
    textAlign: "center",
    marginTop: "80px",
    color: "#e0e0e0",
    fontSize: "1.25rem",
  }),
};
